'use client';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/shadcn/dropdown-menu';
import { cn } from '@/utils/cn';
import {
  BookOpenIcon,
  ChevronDown,
  ChevronUpIcon,
  CircleHelpIcon,
  LogOutIcon,
  PanelLeftIcon,
  PlusIcon,
  SettingsIcon,
  UserIcon
} from 'lucide-react';
import Link from 'next/link';
import {
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarHeader,
  SidebarHeading,
  SidebarItem,
  SidebarLabel,
  SidebarSection,
  SidebarSpacer
} from '../catalyst/sidebar';
import { Button } from '../ui/Button';
import CreateTeamDialog from './CreateTeamDialog';
import { useAccountNavData } from './useAccountNavData';

export default function AccountNav({
  collapsed,
  setCollapsed
}: {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
}) {
  const {
    user,
    signOut,
    openUserProfile,
    workspace,
    inactiveWorkspaces,
    teamCount,
    isLoaded,
    activateWorkspace,
    createTeamOpen,
    setCreateTeamOpen,
    pathname,
    primaryNav,
    recentInterviews
  } = useAccountNavData();

  // Interviews on its own has nothing to be distinguished from, so the active
  // treatment only earns its place once the group holds more than one link.
  const showActiveNav = primaryNav.length > 1;
  const settingsCurrent = pathname.startsWith('/account/settings');

  if (!isLoaded) return null;
  if (!user) return null;

  return (
    <>
      <Sidebar
        className={cn(
          'overflow-hidden whitespace-nowrap transition-[width] duration-300 ease-in-out',
          collapsed ? 'w-16' : 'w-64'
        )}
      >
        <SidebarHeader className={cn(collapsed && '!p-3')}>
          <div className="mb-3 flex w-full justify-center">
            {collapsed ? (
              <img
                src={'/brand/mark-orange.svg'}
                alt={'Corvus'}
                className="mt-2 h-[24px] cursor-pointer"
                onClick={() => setCollapsed(false)}
              />
            ) : (
              <div className="flex w-full flex-row items-center justify-center">
                <div className="flex-1">
                  <Link href="/account">
                    <img
                      src={'/brand/logo.svg'}
                      alt={'Corvus'}
                      className="my-2 ml-2 h-[26px]"
                    />
                  </Link>
                </div>
                <div
                  className="my-auto ml-auto cursor-pointer rounded-md  p-2 text-muted-foreground hover:text-foreground"
                  onClick={() => setCollapsed(true)}
                >
                  <PanelLeftIcon size={18} />
                </div>
              </div>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarItem className="mb-2.5 rounded-lg border border-border bg-background shadow-sm">
                {!collapsed && (
                  <>
                    <SidebarLabel className="pl-2">
                      {workspace?.name}
                    </SidebarLabel>
                    <ChevronDown className="ml-auto mr-2 size-4 text-muted-foreground" />
                  </>
                )}
                {collapsed && (
                  <SidebarLabel className="w-full text-center text-xl font-medium text-foreground">
                    {workspace?.name.slice(0, 1).toUpperCase()}
                  </SidebarLabel>
                )}
              </SidebarItem>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="min-w-64" align="start">
              {(inactiveWorkspaces ?? []).map((ws) => (
                <DropdownMenuItem
                  onClick={() => activateWorkspace(ws)}
                  key={ws.id}
                >
                  {ws.name}

                  {ws.personal && (
                    <>
                      {' '}
                      <UserIcon className="inline text-foreground" size={14} />
                    </>
                  )}
                </DropdownMenuItem>
              ))}
              {(inactiveWorkspaces?.length ?? 0) > 0 && (
                <DropdownMenuSeparator />
              )}
              <DropdownMenuItem onClick={() => setCreateTeamOpen(true)}>
                <PlusIcon className="mr-2 text-muted-foreground" size={14} />
                <span className="text-foreground">Create Team</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarHeader>
        <SidebarBody>
          <SidebarSection>
            <Link href="/account/interview/add">
              <Button variant="dark" className="w-full" size="sm">
                <PlusIcon className="inline text-muted-foreground" size={14} />
                {!collapsed && <>Create Interview</>}
              </Button>
            </Link>
          </SidebarSection>
          <SidebarSection>
            {primaryNav.map((link) => {
              const current = showActiveNav && pathname.startsWith(link.href);
              return (
                <SidebarItem href={link.href} current={current} key={link.href}>
                  <link.icon
                    className={cn(
                      current ? 'text-foreground' : 'text-muted-foreground'
                    )}
                    size={18}
                    strokeWidth={current ? 2.5 : 2}
                  />
                  {!collapsed && <SidebarLabel>{link.label}</SidebarLabel>}
                </SidebarItem>
              );
            })}
          </SidebarSection>
          {!collapsed && recentInterviews.length > 0 && (
            <SidebarSection>
              <SidebarHeading>RECENT INTERVIEWS</SidebarHeading>
              {recentInterviews.map((interview) => (
                <SidebarItem
                  key={interview.id}
                  href={`/account/interview/${interview.slug}`}
                  current={pathname === `/account/interview/${interview.slug}`}
                >
                  <SidebarLabel>{interview.name}</SidebarLabel>
                </SidebarItem>
              ))}
            </SidebarSection>
          )}
          <SidebarSpacer />
          <SidebarSection>
            <SidebarItem href="/docs">
              <BookOpenIcon className="text-muted-foreground" size={18} />
              {!collapsed && <SidebarLabel>Docs</SidebarLabel>}
            </SidebarItem>
            <SidebarItem href="mailto:help@meetcorvus.ai">
              <CircleHelpIcon className="text-muted-foreground" size={18} />
              {!collapsed && <SidebarLabel>Support</SidebarLabel>}
            </SidebarItem>
            {/* Billing, API keys, and Branding are reached from inside
                Settings, which keeps this section to two entries. */}
            {!workspace?.personal && (
              <SidebarItem href="/account/settings" current={settingsCurrent}>
                <SettingsIcon
                  className={cn(
                    settingsCurrent
                      ? 'text-foreground'
                      : 'text-muted-foreground'
                  )}
                  size={18}
                  strokeWidth={settingsCurrent ? 2.5 : 2}
                />
                {!collapsed && <SidebarLabel>Settings</SidebarLabel>}
              </SidebarItem>
            )}
          </SidebarSection>
        </SidebarBody>
        <SidebarFooter>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarItem>
                {!collapsed && (
                  <>
                    <span className="flex min-w-0 items-center gap-3">
                      <span className="min-w-0">
                        <span className="block truncate text-sm/5 font-medium text-foreground">
                          {/* {user} */}
                        </span>
                        <span className="block truncate text-xs/5 font-normal text-muted-foreground">
                          {user.primaryEmailAddress?.emailAddress}
                        </span>
                      </span>
                    </span>
                    <div className="ml-auto">
                      <ChevronUpIcon />
                    </div>
                  </>
                )}
                {collapsed && <UserIcon size={18} />}
              </SidebarItem>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="min-w-64" side="top" align="start">
              <DropdownMenuItem onClick={() => openUserProfile()}>
                <UserIcon className="mr-2 text-muted-foreground" size={16} />
                <span className="text-foreground">User Settings</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => signOut({ redirectUrl: '/' })}>
                <LogOutIcon className="mr-2 text-muted-foreground" size={16} />
                <span className="text-foreground">Sign out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarFooter>
      </Sidebar>
      <CreateTeamDialog
        isOpen={createTeamOpen}
        setIsOpen={setCreateTeamOpen}
        currentTeamCount={teamCount}
      />
    </>
  );
}
